import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { CreateLabelRequest, Label, UpdateLabelRequest } from '@core/models/labels.model';
import { CreateTagRequest, Tag, UpdateTagRequest } from '@core/models/tags.model';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

@Injectable({
  providedIn: 'root'
})
export class TagsLabelsService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);

  private buildUrl(path: string): string {
    return `${this.gbeUrl}/${path}`;
  }

  // Tags

  listTags$(dataSpaceId?: number): Observable<Tag[]> {
    return this.http
      .get<{ tags: Tag[] }>(this.buildUrl(GbeEndPointV4.TAGS), {
        params: dataSpaceId ? { data_space_id: dataSpaceId } : {}
      })
      .pipe(map((response) => response.tags));
  }

  createTag$(data: CreateTagRequest): Observable<Tag> {
    return this.http
      .post<{ tag: Tag }>(this.buildUrl(GbeEndPointV4.TAGS), data)
      .pipe(map((response) => response.tag));
  }

  updateTag$(id: string, data: UpdateTagRequest): Observable<Tag> {
    return this.http
      .patch<{ tag: Tag }>(this.buildUrl(`${GbeEndPointV4.TAGS}/${id}`), data)
      .pipe(map((response) => response.tag));
  }

  deleteTag$(id: string): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${GbeEndPointV4.TAGS}/${id}`));
  }

  // Labels

  listLabels$(entityTypeFilter?: string, dataSpaceId?: number): Observable<Label[]> {
    const params: Record<string, string> = {};
    if (entityTypeFilter) {
      params['entity_type_filter'] = entityTypeFilter;
    }
    if (dataSpaceId) {
      params['data_space_id'] = dataSpaceId.toString();
    }

    return this.http
      .get<{ labels: Label[] }>(this.buildUrl(GbeEndPointV4.LABELS), { params })
      .pipe(map((response) => response.labels));
  }

  createLabel$(data: CreateLabelRequest): Observable<Label> {
    return this.http
      .post<{ label: Label }>(this.buildUrl(GbeEndPointV4.LABELS), data)
      .pipe(map((response) => response.label));
  }

  updateLabel$(id: string, data: UpdateLabelRequest): Observable<Label> {
    return this.http
      .patch<{ label: Label }>(this.buildUrl(`${GbeEndPointV4.LABELS}/${id}`), data)
      .pipe(map((response) => response.label));
  }

  deleteLabel$(id: string): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${GbeEndPointV4.LABELS}/${id}`));
  }
}
